import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Activity, CircleAlert, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/layout/PageHeader";
import { Card } from "@/components/ui/Card";
import { useAuth } from "@/hooks/useAuth";
import { dateLocale, t } from "@/lib/i18n";
import type { LogEntry, LogResponse } from "@/lib/types";

/**
 * Eine Störung = eine Trace-Gruppe, in der mindestens ein ERROR/FATAL steckt.
 * Wir gruppieren dieselben Logs wie die Traces-Ansicht, filtern aber auf
 * Fehler und labeln die Teilnehmer als „Geräte" statt „Services".
 */
type Incident = {
  incidentId: string;
  devices: string[];
  errorCount: number;
  startTs: string;
  endTs: string;
  topMessage: string;
};

const LOOKBACK = 500;

function toIncidents(logs: LogEntry[]): Incident[] {
  const grouped = new Map<string, LogEntry[]>();
  for (const log of logs) {
    if (!log.trace_id) continue;
    const arr = grouped.get(log.trace_id);
    if (arr) arr.push(log);
    else grouped.set(log.trace_id, [log]);
  }
  const out: Incident[] = [];
  for (const [incidentId, entries] of grouped) {
    const errs = entries.filter((e) => e.level === "ERROR" || e.level === "FATAL");
    if (errs.length === 0) continue; // nur Störungen
    entries.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
    const devices = Array.from(
      new Set(entries.map((e) => e.service ?? e.client_id).filter(Boolean) as string[]),
    );
    out.push({
      incidentId,
      devices,
      errorCount: errs.length,
      startTs: entries[0].timestamp,
      endTs: entries[entries.length - 1].timestamp,
      topMessage: errs[0].message,
    });
  }
  return out.sort((a, b) => new Date(b.endTs).getTime() - new Date(a.endTs).getTime());
}

export function ZeitstrahlListPage() {
  const { token } = useAuth();
  const [incidents, setIncidents] = useState<Incident[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await api.get<LogResponse>(`/api/logs?limit=${LOOKBACK}&include_total=false`);
      setIncidents(toIncidents(resp.logs));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return (
    <div>
      <PageHeader
        title={t("Zeitstrahl")}
        description={t("Störungen als Logikanalysator-Replay — ein Cursor über alle Geräte und Signale.")}
        actions={
          <button
            type="button"
            onClick={reload}
            disabled={loading}
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1.5 text-xs hover:bg-muted disabled:opacity-60"
          >
            <RefreshCw className={"h-3.5 w-3.5 " + (loading ? "animate-spin" : "")} />
            {t("Aktualisieren")}
          </button>
        }
      />

      <div className="flex flex-col gap-4 p-6">
        {error ? (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            {error}
          </div>
        ) : null}

        <Card title={t("So funktioniert's")} description={t("Störungen aus der Zelle (Blackbox).")}>
          <p className="text-sm leading-relaxed text-muted-foreground">
            {t(
              "Diese Liste sammelt Störungen (Trace-Gruppen mit mindestens einem ERROR) aus den letzten {n} Logs. Klick auf eine Störung öffnet das Replay (Swimlanes, DI/DO-Signalspuren, Kamera-Scrub, Gutlauf-Diff). Die Signaldaten liefert Blackbox pro Störfall: Events als Logs mit gemeinsamer",
              { n: LOOKBACK },
            )}{" "}
            <span className="font-mono">trace_id</span> {t("plus das Signalfenster")} (
            <span className="font-mono">PUT /api/zeitstrahl/&#123;id&#125;/window</span>).
          </p>
        </Card>

        <Card title={t("Störungen ({n})", { n: incidents?.length ?? 0 })}>
          {incidents == null ? (
            <div className="text-sm text-muted-foreground">{t("Lade…")}</div>
          ) : incidents.length === 0 ? (
            <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
              {t("Keine Störungen mit")} <span className="font-mono">trace_id</span>{" "}
              {t("in den letzten {n} Logs.", { n: LOOKBACK })}
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {incidents.map((i) => (
                <li key={i.incidentId}>
                  <Link
                    to={`/zeitstrahl/${encodeURIComponent(i.incidentId)}`}
                    className="grid grid-cols-[1fr_max-content] items-center gap-3 px-2 py-2.5 hover:bg-muted/40"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <Activity className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                        <span className="truncate text-sm">{i.topMessage}</span>
                        <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-destructive/15 px-2 py-0.5 text-[10px] font-medium text-destructive">
                          <CircleAlert className="h-2.5 w-2.5" /> {i.errorCount}
                        </span>
                      </div>
                      <div className="mt-0.5 truncate font-mono text-xs text-muted-foreground">
                        {i.devices.join(" · ") || "—"} · {i.incidentId}
                      </div>
                    </div>
                    <div className="text-right text-xs text-muted-foreground">
                      {new Date(i.endTs).toLocaleString(dateLocale())}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}
